"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QueryPredicateBuilder = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const react_beautiful_dnd_1 = require("react-beautiful-dnd");
const rebass_1 = require("rebass");
const predicate_1 = require("../../../parser/src/predicate");
const booleanExpressionFunctions_1 = require("../../../Utilities/ExpressionFunctions/booleanExpressionFunctions");
const AdaptableContext_1 = require("../../../View/AdaptableContext");
const DropdownButton_1 = tslib_1.__importDefault(require("../../DropdownButton"));
const ErrorBox_1 = tslib_1.__importDefault(require("../../ErrorBox"));
const icons_1 = require("../../icons");
const SimpleButton_1 = tslib_1.__importDefault(require("../../SimpleButton"));
const QueryBuilderInputs_1 = require("./QueryBuilderInputs");
const utils_1 = require("./utils");
const ITEM_HEIGHT = 40;
const BASE_CLASS_NAME = 'ab-QueryBuilder-predicate-editor';
const Handle = (props) => (react_1.default.createElement(rebass_1.Flex, Object.assign({ className: `${BASE_CLASS_NAME}__handle`, height: ITEM_HEIGHT, alignItems: "center", mr: 1 }, props),
    react_1.default.createElement(icons_1.Icon, { name: "drag" })));
const QueryPredicateButtons = (props) => {
    return (react_1.default.createElement(react_1.default.Fragment, null,
        !props.hideAdd && (react_1.default.createElement(DropdownButton_1.default, { listMinWidth: 150, columns: ['label'], items: [
                { label: 'Add Column Condition', onClick: () => props.onNewPredicate('filter') },
                { label: 'Add AND / OR Group', onClick: () => props.onNewPredicate('group') },
            ], variant: "text" },
            react_1.default.createElement(icons_1.Icon, { name: "plus" }))),
        !props.hideDelete && (react_1.default.createElement(SimpleButton_1.default, { icon: "delete", variant: "text", onClick: () => {
                props.onChange(null);
            } }))));
};
const LogicalFunctionEditor = (props) => {
    const level = props.id.split('/').length - 1;
    const className = `
    ${BASE_CLASS_NAME}
    ${BASE_CLASS_NAME}-level-${level}
    ${BASE_CLASS_NAME}-combinator
    ${props.lastChild ? `${BASE_CLASS_NAME}--last-child` : ''}
    ${props.isRoot ? `${BASE_CLASS_NAME}--root` : `${BASE_CLASS_NAME}--child`}
  `;
    const getCombinatorEl = (handleProps, className) => (react_1.default.createElement(react_beautiful_dnd_1.Droppable, { droppableId: props.id, type: props.id }, (provided, snapshot) => {
        return (react_1.default.createElement("div", Object.assign({}, provided.droppableProps, { ref: provided.innerRef, className: className }),
            react_1.default.createElement(rebass_1.Flex, null,
                props.isRoot ? null : react_1.default.createElement(Handle, Object.assign({}, handleProps)),
                react_1.default.createElement(rebass_1.Flex, { flex: 1, alignItems: "center", height: ITEM_HEIGHT },
                    react_1.default.createElement(QueryBuilderInputs_1.CombinatorSelector, { value: props.predicate.operator, onChange: (combinator) => {
                            props.onChange(Object.assign(Object.assign({}, props.predicate), { operator: combinator }));
                        } }),
                    react_1.default.createElement(rebass_1.Box, { flex: 1 }),
                    react_1.default.createElement(QueryPredicateButtons, Object.assign({ hideDelete: props.isRoot, hideAdd: true }, props)))),
            react_1.default.createElement(rebass_1.Box, { className: `${BASE_CLASS_NAME}__children-wrapper` },
                props.predicate.args.map((arg, index) => {
                    const id = `${props.id}/${index}`;
                    return (react_1.default.createElement(exports.QueryPredicateBuilder, { key: id, lastChild: index === props.predicate.args.length - 1, index: index, id: id, predicate: arg, onNewPredicate: (type) => {
                            const newPredicate = {
                                operator: type === 'filter' ? undefined : 'AND',
                                args: [],
                            };
                            if (typeof arg === 'object' &&
                                'operator' in arg &&
                                (0, predicate_1.isQlLogicalOperator)(arg.operator)) {
                                // add as a child
                                const newArg = Object.assign(Object.assign({}, arg), { args: [...arg.args, newPredicate] });
                                const args = [...props.predicate.args];
                                args[index] = newArg;
                                props.onChange(Object.assign(Object.assign({}, props.predicate), { args }));
                            }
                            else {
                                // add as a sibling
                                const prevArgs = [...props.predicate.args];
                                prevArgs.splice(index + 1, 0, newPredicate);
                                props.onChange(Object.assign(Object.assign({}, props.predicate), { args: prevArgs }));
                            }
                        }, onChange: (predicate) => {
                            const args = [...props.predicate.args];
                            if (predicate) {
                                args[index] = predicate;
                            }
                            else {
                                args.splice(index, 1);
                            }
                            props.onChange(Object.assign(Object.assign({}, props.predicate), { args }));
                        } }));
                }),
                provided.placeholder,
                react_1.default.createElement("div", { className: `${BASE_CLASS_NAME}__root-actions` },
                    react_1.default.createElement(QueryPredicateButtons, Object.assign({}, props, { hideDelete: true }))))));
    }));
    if (props.isRoot) {
        return getCombinatorEl({ className: className });
    }
    else {
        return (react_1.default.createElement(react_beautiful_dnd_1.Draggable, { key: props.id, draggableId: props.id, index: props.index }, (provided, snapshot) => {
            return (react_1.default.createElement("div", Object.assign({}, provided.draggableProps, { ref: provided.innerRef, className: className }), getCombinatorEl(provided.dragHandleProps)));
        }));
    }
};
const PrimitiveFunctionEditor = (props) => {
    var _a;
    // [handle] [column] [operator-dropdown] [...args] [delete-button] [plus-button]
    const adaptable = (0, AdaptableContext_1.useAdaptable)();
    const [columnExpression, ...restOfArgs] = props.predicate.args;
    const col = columnExpression;
    let columnId = null;
    let columnDataType = null;
    let columnInputDataType = null;
    let functionInputInputDataTypes = null;
    // Thsese are the type of inputs ommiting the column
    // [[column-data-type], number, number]
    let restOfFunctionInputDataTypes = [];
    if (col) {
        if (typeof col !== 'string' || !col.includes('[')) {
            return react_1.default.createElement(ErrorBox_1.default, null, "Expression must start with a column!");
        }
        columnId = col.replace(/[\[\]]/g, '');
        columnDataType = adaptable.api.columnApi.getColumnDataTypeForColumnId(columnId);
        columnInputDataType = (0, utils_1.mapColumnDataTypeToExpressionFunctionType)(columnDataType);
        functionInputInputDataTypes = (_a = booleanExpressionFunctions_1.booleanExpressionFunctions[props.predicate.operator]) === null || _a === void 0 ? void 0 : _a.inputs;
        restOfFunctionInputDataTypes = functionInputInputDataTypes
            ? (0, utils_1.getOperatorMatchingInputs)(columnInputDataType, functionInputInputDataTypes)
            : [];
    }
    const level = props.id.split('/').length - 1;
    return (react_1.default.createElement(react_beautiful_dnd_1.Draggable, { key: props.id, draggableId: props.id, index: props.index }, (provided) => {
        return (react_1.default.createElement(rebass_1.Flex, Object.assign({ className: `
              ${BASE_CLASS_NAME} 
              ${BASE_CLASS_NAME}-level-${level}
              ${BASE_CLASS_NAME}-primitive
              ${props.lastChild ? `${BASE_CLASS_NAME}--last-child` : ''}
            `, pb: 2, ref: provided.innerRef }, provided.draggableProps, { style: Object.assign(Object.assign({}, provided.draggableProps.style), { minHeight: ITEM_HEIGHT }) }),
            react_1.default.createElement(Handle, Object.assign({}, provided.dragHandleProps)),
            react_1.default.createElement(rebass_1.Flex, { alignItems: "center", height: ITEM_HEIGHT },
                react_1.default.createElement(rebass_1.Box, { mr: 2 },
                    react_1.default.createElement(QueryBuilderInputs_1.PrimitiveColumnSelector, { onChange: (colId) => {
                            props.onChange(Object.assign(Object.assign({}, props.predicate), { args: [colId], operator: null }));
                        }, columnId: columnId })),
                columnId && columnDataType && (react_1.default.createElement(QueryBuilderInputs_1.ExpressionSelector, { dataType: columnDataType, onExpressionChange: (operator) => {
                        let args = [props.predicate.args[0]];
                        if (columnDataType === 'Boolean' && operator !== 'NOT') {
                            args = [props.predicate.args[0], 'TRUE'];
                        }
                        props.onChange(Object.assign(Object.assign({}, props.predicate), { operator,
                            // discard arguments
                            args }));
                    }, value: props.predicate.operator })),
                react_1.default.createElement(rebass_1.Flex, { flex: 1, ml: 2 }, restOfFunctionInputDataTypes.map((type, index) => {
                    var _a;
                    let value = restOfArgs[index];
                    const commonProps = {
                        key: type + index,
                        lefthandColumnIdParam: columnId,
                        inputType: type,
                    };
                    if (type.includes('[]')) {
                        return (react_1.default.createElement(QueryBuilderInputs_1.PrimitiveMultiValueInput, Object.assign({}, commonProps, { value: restOfArgs, onChange: (values) => {
                                const args = [...props.predicate.args.slice(0, 1), ...values];
                                props.onChange(Object.assign(Object.assign({}, props.predicate), { args }));
                            } })));
                    }
                    return (react_1.default.createElement(QueryBuilderInputs_1.PrimiteValueInput, Object.assign({}, commonProps, { value: (_a = restOfArgs[index]) !== null && _a !== void 0 ? _a : null, onChange: (value) => {
                            const args = [...props.predicate.args];
                            // +1 because col is the first argument
                            args[index + 1] = value;
                            props.onChange(Object.assign(Object.assign({}, props.predicate), { args }));
                        } })));
                }))),
            react_1.default.createElement(rebass_1.Box, { flex: 1 }),
            react_1.default.createElement(QueryPredicateButtons, Object.assign({}, props))));
    }));
};
/**
 * Two types:
 * - combinatory operator
 * - can contain both expressons and combinators
 * [handle] [combinator-dropdown]
 *   [children]
 *
 * - boolean function: ars do not contain other combinators
 * [handle] [column] [operator-dropdown] [...args] [delete-button] [plus-button]
 */
const QueryPredicateBuilder = (props) => {
    if ((0, predicate_1.isQlLogicalOperator)(props.predicate.operator)) {
        return react_1.default.createElement(LogicalFunctionEditor, Object.assign({}, props));
    }
    else {
        return react_1.default.createElement(PrimitiveFunctionEditor, Object.assign({}, props));
    }
};
exports.QueryPredicateBuilder = QueryPredicateBuilder;
